import { useEffect, useRef, useState } from "react";
import { newsClient, newsImageUrl } from "../apiClient/newsClient";
import { ApiError } from "../apiClient/apiRequest";
import type { NewsPost } from "../types/news.types";

const MAX_IMAGE_MB = 2;

function ImageControl({ post, onChanged }: { post: NewsPost; onChanged: () => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later (e.g. after a failed attempt)
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Image must be an image file.");
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      setError(`Image is too large — the limit is ${MAX_IMAGE_MB}MB.`);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await newsClient.uploadImage(post.id, file);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't upload this image.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    setBusy(true);
    setError(null);
    try {
      await newsClient.removeImage(post.id);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't remove this image.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="benefit-attachment-control">
      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileChosen} />
      {post.imageBlobName ? (
        <span className="benefit-attachment-existing">
          <img className="news-admin-image-preview" src={newsImageUrl(post.id)} alt="" />
          <button type="button" className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={busy}>
            Replace image
          </button>
          <button type="button" className="btn btn-danger-ghost" onClick={handleRemove} disabled={busy}>
            Remove
          </button>
        </span>
      ) : (
        <button type="button" className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={busy}>
          {busy ? "Uploading…" : "Add image"}
        </button>
      )}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

function PostRow({ post, onChanged }: { post: NewsPost; onChanged: () => void }) {
  const [title, setTitle] = useState(post.title);
  const [body, setBody] = useState(post.body);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDirty = title.trim() !== post.title || body.trim() !== post.body;

  async function handleSave() {
    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    if (!trimmedTitle || !trimmedBody) {
      setError("Title and body can't be empty — delete the post instead.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await newsClient.updatePost(post.id, trimmedTitle, trimmedBody);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save this.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    setSubmitting(true);
    setError(null);
    try {
      await newsClient.deletePost(post.id);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete this.");
      setSubmitting(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <li className="about-admin-row">
      <div className="form-field">
        <label>Title</label>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} disabled={submitting} />
      </div>
      <textarea
        className="about-admin-textarea"
        rows={5}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        disabled={submitting}
      />
      <ImageControl post={post} onChanged={onChanged} />
      <div className="about-admin-row-actions">
        {confirmingDelete ? (
          <div className="cancel-confirm">
            <span>Delete?</span>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmingDelete(false)} disabled={submitting}>
              No
            </button>
            <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={submitting}>
              {submitting ? "Deleting…" : "Yes"}
            </button>
          </div>
        ) : (
          <>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={submitting || !isDirty}>
              {submitting ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn btn-danger-ghost" onClick={() => setConfirmingDelete(true)} disabled={submitting}>
              Delete
            </button>
          </>
        )}
      </div>
      {error && <span className="field-error">{error}</span>}
    </li>
  );
}

export function NewsAdminPage() {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  function refresh() {
    setIsLoading(true);
    setError(null);
    newsClient
      .getPosts()
      .then(setPosts)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load news."))
      .finally(() => setIsLoading(false));
  }

  useEffect(refresh, []);

  async function handleAdd() {
    const trimmedTitle = newTitle.trim();
    const trimmedBody = newBody.trim();
    if (!trimmedTitle || !trimmedBody) {
      setAddError("Provide a title and body first.");
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await newsClient.createPost(trimmedTitle, trimmedBody);
      setNewTitle("");
      setNewBody("");
      refresh();
    } catch (err) {
      setAddError(err instanceof ApiError ? err.message : "Couldn't add this post.");
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Manage news</h1>
          <p className="page-sub">Publish, edit, or remove posts shown on the News page and the home page preview.</p>
        </div>
      </header>

      <div className="form-card">
        <h2>Publish a post</h2>
        <div className="form">
          <div className="form-field">
            <label>Title</label>
            <input type="text" placeholder="What's the headline?" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
          </div>
          <div className="form-field">
            <textarea rows={5} placeholder="Post body…" value={newBody} onChange={(e) => setNewBody(e.target.value)} />
          </div>
          {addError && <div className="form-banner form-banner--error">{addError}</div>}
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={handleAdd} disabled={adding}>
              {adding ? "Publishing…" : "Publish"}
            </button>
          </div>
        </div>
      </div>

      <h2 className="admin-section-heading">Published posts</h2>
      {error ? (
        <div className="state-panel">
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="state-panel">
          <p>No posts yet — publish the first one above.</p>
        </div>
      ) : (
        <ul className="about-admin-list">
          {posts.map((p) => (
            <PostRow key={p.id} post={p} onChanged={refresh} />
          ))}
        </ul>
      )}
    </div>
  );
}
